// 인증 컨텍스트 (Bearer 우선, 세션스토리지 보관)
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from './api'

export interface MeUser {
  userId: string
  tenantId: string
  email: string
  roles: string[]
  permissions: string[]
}

interface LoginResp {
  userId: string
  email: string
  tenantId: string
  accessToken: string
  expiresIn: number
  csrfToken: string
}

interface AuthCtx {
  user: MeUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}
const Ctx = createContext<AuthCtx>({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
})

export function useAuth() { return useContext(Ctx) }

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!api.getToken()) {
      setUser(null)
      setLoading(false)
      return
    }
    try {
      const me = await api.get<MeUser>('/auth/me')
      setUser(me)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    // 다른 탭/세션에서 토큰 변경 감지
    const off = api.onTokenChange((t) => { if (!t) setUser(null) })
    return () => { off() }
  }, [refresh])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResp>('/auth/login', { email, password })
    api.setToken(res.accessToken, res.expiresIn)
    await refresh()
  }, [refresh])

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout') } catch { /* ignore */ }
    api.setToken(null)
    setUser(null)
  }, [])

  return <Ctx.Provider value={{ user, loading, login, logout, refresh }}>{children}</Ctx.Provider>
}

export function hasPerm(user: MeUser | null, resource: string, action: 'read' | 'write' | 'admin') {
  if (!user) return false
  if (user.permissions.includes('*:admin')) return true
  if (user.permissions.includes(`${resource}:${action}`)) return true
  if (action === 'read' && (user.permissions.includes(`${resource}:write`) || user.permissions.includes(`${resource}:admin`))) return true
  if (action === 'write' && user.permissions.includes(`${resource}:admin`)) return true
  return false
}
